import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Phone, Car, Bike, Zap, Truck, Eye, Edit, Trash2, Calendar, Gauge, Fuel, Settings, Package, IndianRupee, MessageCircle, Loader2, User, FileText, CheckCircle, Clock, XCircle, TrendingUp, Heart, ChevronRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FaUserTie } from "react-icons/fa6";
import { HiMiniViewfinderCircle } from "react-icons/hi2";
import { FiAlertOctagon } from "react-icons/fi";
import { TfiList } from "react-icons/tfi";
import { RiChatHistoryLine } from "react-icons/ri";
import { RiMedal2Fill } from "react-icons/ri";
import VehicleVerificationTab from './Vehicleverificationtab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [vehicles, setVehicles] = useState([]);
  const [myPayments, setMyPayments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('listings');
  const [selectedBill, setSelectedBill] = useState(null);
  const [showBillDialog, setShowBillDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [citiesRes, vehiclesRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/vehicles/seller/my-vehicles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payments/my-payments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCities(citiesRes.data.cities);
      setVehicles(vehiclesRes.data.vehicles || []);
      setMyPayments(paymentsRes.data.payments || []);
      
      // Calculate stats
      const allVehicles = vehiclesRes.data.vehicles || [];
      setStats({
        total: allVehicles.length,
        pending: allVehicles.filter(v => v.status === 'pending').length,
        approved: allVehicles.filter(v => v.status === 'approved').length,
        rejected: allVehicles.filter(v => v.status === 'rejected').length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!vehicleToDelete) return;
    
    try {
      await axios.delete(`${API}/vehicles/${vehicleToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vehicle deleted successfully');
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const getVehicleIcon = (type) => {
    switch(type) {
      case 'car': return <Car className="h-5 w-5" />;
      case 'bike': return <Bike className="h-5 w-5" />;
      case 'ev': return <Zap className="h-5 w-5" />;
      case 'commercial': return <Truck className="h-5 w-5" />;
      default: return <Car className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-700 border-green-200',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-gray-700 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-grid-pattern" />
        <div className="relative px-4 md:px-8 lg:px-12 py-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/20 to-white/10 flex items-center justify-center shadow-lg backdrop-blur-sm">
                <FaUserTie className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  Agent Dashboard
                </h1>
                <p className="text-purple-200 mt-1">Welcome back, {user?.name}! Manage your vehicle inventory.</p>
                {user?.agent_uid && (
                      <span className="inline-flex items-center gap-1.5 mt-2 font-mono text-[11px]
                                      font-semibold text-white/90 bg-white/10 border border-white/20
                                      px-2.5 py-1 rounded-lg select-all backdrop-blur-sm">
                        {/* badge icon */}
                        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                          <rect x="1" y="1" width="14" height="14" rx="3"
                                fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.6)"
                                strokeWidth="1.2"/>
                          <path d="M5 5h6M5 8h4M5 11h6" stroke="white"
                                strokeWidth="1.4" strokeLinecap="round"/>
                        </svg>
                        {user.agent_uid}
                      </span>
                    )}
              </div>
            </div>
            <Button 
              onClick={() => navigate('/upload-vehicle')}
              className="bg-white text-orange-700 hover:bg-orange-50 px-6 py-3 text-base font-semibold shadow-lg"
              data-testid="add-vehicle-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add New Vehicle
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 py-8">
        {/* Stats Grid */}
        <motion.div 
  variants={containerVariants}
  initial="hidden"
  animate="visible"
  className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10"
>
  {[
    { label: 'Total Vehicles', value: stats.total, icon: TfiList },
    { label: 'Live & Approved', value: stats.approved, icon: RiMedal2Fill },
    { label: 'Pending Review', value: stats.pending, icon: RiChatHistoryLine },
    { label: 'Rejected', value: stats.rejected, icon: FiAlertOctagon }
  ].map((stat, idx) => (
    <motion.div key={idx} variants={itemVariants}>
      <Card className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
        
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            
            {/* Left */}
            <div>
              <p className="text-sm text-gray-500">
                {stat.label}
              </p>
              <h2 className="text-2xl font-semibold text-gray-900 mt-1">
                {stat.value}
              </h2>
            </div>

            {/* Right Icon */}
            <div className="p-2 rounded-lg bg-gray-100">
              <stat.icon className="h-5 w-5 text-gray-600" />
            </div>

          </div>
        </CardContent>

      </Card>
    </motion.div>
  ))}
</motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-gray-200">
          {[
            { id: 'listings', label: 'My Vehicles', icon: Car },
            { id: 'payments', label: 'Payment History', icon: FileText },
            { id: 'verification', label: 'Vehicle Verification', icon: Shield }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
                activeTab === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Section Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">My Vehicle Listings</h2>
              <Button 
                onClick={() => navigate('/upload-vehicle')}
                variant="outline"
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Vehicle
              </Button>
            </div>

            {vehicles.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                    <Car className="h-10 w-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Vehicles Listed</h3>
                  <p className="text-gray-500 mb-6">Start adding vehicles to your inventory</p>
                  <Button onClick={() => navigate('/upload-vehicle')} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="h-4 w-4 mr-2" /> Add Your First Vehicle
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vehicles.map((vehicle, idx) => (
                  <motion.div
                    key={vehicle.id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card 
                      className="overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer border-0 bg-white"
                      onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                      data-testid={`agent-vehicle-${idx}`}
                    >
                      {/* Vehicle Image */}
                      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                        {vehicle.images && vehicle.images[0] ? (
                          <img 
                            src={vehicle.images[0]} 
                            alt={`${vehicle.brand_name} ${vehicle.model_name}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {getVehicleIcon(vehicle.vehicle_type)}
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <Badge className={`${getStatusBadge(vehicle.status)} capitalize`}>
                            {vehicle.status}
                          </Badge>
                        </div>
                        <div className="absolute top-3 right-3">
                          <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm">
                            {getVehicleIcon(vehicle.vehicle_type)}
                            <span className="ml-1 capitalize">{vehicle.vehicle_type}</span>
                          </Badge>
                        </div>
                        {/* Price Tag */}
                        <div className="absolute bottom-3 right-3">
                          <div className="bg-slate-100 text-amber-600 px-4 py-2 rounded-full font-bold shadow-lg">
                            <IndianRupee className="h-4 w-4 inline" />
                            {vehicle.price}
                          </div>
                        </div>
                      </div>

                      <CardContent className="p-5">
                        {/* Title */}
                        <h3 className="font-bold text-lg text-gray-800 mb-1 transition-colors">
                            {vehicle.brand_name} {vehicle.model_name}
                          </h3>
                          <p className="text-sm text-gray-500 mb-1">{vehicle.variant_name}</p>
                          {vehicle.vehicle_display_id && (
                            <span className="inline-block mb-3 text-[10px] font-mono font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                              {vehicle.vehicle_display_id}
                            </span>
                          )}
                        
                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 mb-4">
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span>{vehicle.manufacturing_year}</span>
                          </div>
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                            <Gauge className="h-4 w-4 text-gray-400" />
                            <span>{vehicle.km_run_range}</span>
                          </div>
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                            <Fuel className="h-4 w-4 text-gray-400" />
                            <span>{vehicle.fuel_type}</span>
                          </div>
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                            <Settings className="h-4 w-4 text-gray-400" />
                            <span>{vehicle.transmission}</span>
                          </div>
                        </div>

                        {/* Registration */}
                        <p className="text-xs text-gray-400 mb-4">Reg: {vehicle.registration_no}</p>

                        {/* Contact Buttons */}
                        {/* <div className="flex gap-2 mb-4">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-500 hover:bg-green-600"
                            onClick={(e) => { e.stopPropagation(); window.open(`tel:${vehicle.contact_phone || user?.phone}`, '_self'); }}
                          >
                            <Phone className="h-4 w-4 mr-1" /> Call
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-[#25D366] hover:bg-[#20BD5A]"
                            onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/91${(vehicle.contact_phone || user?.phone).replace(/\D/g, '')}?text=Hi, I'm interested in your ${vehicle.brand_name} ${vehicle.model_name}`, '_blank'); }}
                          >
                            <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                          </Button>
                        </div> */}

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1"
                            onClick={(e) => { e.stopPropagation(); navigate(`/vehicles/${vehicle.id}`); }}
                          >
                            <HiMiniViewfinderCircle className="h-4 w-4 mr-1" /> View
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1"
                            onClick={(e) => { e.stopPropagation(); navigate(`/edit-vehicle/${vehicle.id}`); }}
                          >
                            <Edit className="h-4 w-4 mr-1" /> Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            onClick={(e) => { e.stopPropagation(); setVehicleToDelete(vehicle.id); setDeleteDialogOpen(true); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-500" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myPayments.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No payments found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myPayments.map((payment, idx) => (
                      <div
                        key={payment.id || idx}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors gap-4"
                        data-testid={`payment-${idx}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${
                            payment.status === 'approved' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {payment.status === 'approved' ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <Clock className="h-5 w-5 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{payment.payment_id}</p>
                            <p className="text-sm text-gray-500">{payment.registration_no}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-gray-800">₹{payment.amount}</p>
                            <Badge className={`text-xs ${
                              payment.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {payment.status}
                            </Badge>
                          </div>
                          {payment.status === 'approved' && payment.bill_attachment && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedBill(payment.bill_attachment); setShowBillDialog(true); }}
                              className="border-purple-300 text-purple-600 hover:bg-purple-50"
                              data-testid={`view-bill-${idx}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Bill
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'verification' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{duration:0.3}}>
            <VehicleVerificationTab />
          </motion.div>
        )}
 
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Vehicle</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">Are you sure you want to delete this vehicle? This action cannot be undone.</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" className="flex-1" onClick={handleDelete}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bill Dialog */}
      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment Bill</DialogTitle>
          </DialogHeader>
          {selectedBill && (
            <div className="max-h-[70vh] overflow-auto">
              <img src={selectedBill} alt="Payment Bill" className="w-full rounded-lg" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AgentDashboard;
